/**
 * Hook para atividades de Deal com paginação server-side
 * Usa useInfiniteQuery com cursor composto (created_at + id)
 * 
 * @version 2.0.0
 * RELEASE: 2026-05-10 v2.0.0 - 100% server-side: anexos via get_activity_attachments_batch e mutations via RPCs complete/reopen/update/delete_deal_activity
 * RELEASE: 2026-03-16 v1.0.0 - Implementação inicial
 */
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';
import { useMemo } from 'react';
import type { DealActivity, ActivityType, UpdateActivityInput } from '@/hooks/useCrmDealActivities';
import type { ActivityAttachment } from '@/hooks/useActivityAttachments';

// Logger do módulo
const logger = createLogger('DealActivitiesPaginated');

/** Tamanho da página para scroll infinito */
const PAGE_SIZE = 10;

/**
 * Cursor composto para paginação server-side
 */
interface CursorParam {
  cursor_created_at: string;
  cursor_id: string;
}

/**
 * Opções do hook
 */
interface DealActivitiesPaginatedOptions {
  enabled?: boolean;
}

/**
 * Hook para atividades de um deal com paginação server-side
 * Substitui useCrmDealActivities quando paginated=true
 */
export function useDealActivitiesPaginated(
  dealId: string | null,
  options?: DealActivitiesPaginatedOptions
) {
  const { currentTenant, user } = useAuth();
  const queryClient = useQueryClient();

  const isEnabled = (options?.enabled ?? true) && !!dealId && !!currentTenant?.id;

  // Query infinita com cursor composto
  const infiniteQuery = useInfiniteQuery({
    queryKey: ['crm-deal-activities-paginated', dealId],
    queryFn: async ({ pageParam }) => {
      if (!dealId || !currentTenant?.id) return [];

      const cursor = pageParam as CursorParam | null;
      logger.debug('Buscando página de atividades do deal:', { dealId, cursor });

      // Chama RPC paginada
      const { data, error } = await supabase.rpc('get_deal_activities_paginated', {
        p_deal_id: dealId,
        p_limit: PAGE_SIZE,
        p_cursor_created_at: cursor?.cursor_created_at || null,
        p_cursor_id: cursor?.cursor_id || null,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar atividades paginadas:', error.message);
        throw error;
      }

      // Transforma resultado flat da RPC em DealActivity
      const activities: DealActivity[] = (data || []).map((row: {
        id: string;
        activity_type: string;
        title: string;
        description: string | null;
        scheduled_at: string | null;
        completed_at: string | null;
        is_system: boolean;
        created_at: string;
        performed_by_id: string | null;
        performed_by_name: string | null;
        performed_by_avatar: string | null;
        comments_count: number;
        conferencing_link: string | null; // v1.8.0: retornado pela RPC
        new_value: unknown; // v1.9.0: metadados system
        previous_value: unknown; // v1.9.0
      }) => ({
        id: row.id,
        tenant_id: currentTenant.id,
        deal_id: dealId,
        customer_id: null,
        activity_type: row.activity_type as ActivityType,
        title: row.title,
        description: row.description,
        is_system: row.is_system,
        previous_value: (row.previous_value ?? null) as Record<string, unknown> | null, // v1.9.0
        new_value: (row.new_value ?? null) as Record<string, unknown> | null, // v1.9.0
        scheduled_at: row.scheduled_at,
        completed_at: row.completed_at,
        performed_by: row.performed_by_id,
        created_at: row.created_at,
        updated_at: row.created_at,
        performer: row.performed_by_id ? {
          id: row.performed_by_id,
          full_name: row.performed_by_name,
          avatar_url: row.performed_by_avatar,
        } : null,
        comments_count: row.comments_count || 0,
        conferencing_link: row.conferencing_link || null, // v1.8.0: direto da RPC
        attachments: [] as ActivityAttachment[],
      }));

      // v2.0.0: anexos via RPC server-side (eliminado SELECT direto a crm_activity_attachments)
      if (activities.length > 0) {
        const activityIds = activities.map(a => a.id);
        const { data: attachmentsData, error: attErr } = await supabase.rpc(
          'get_activity_attachments_batch',
          { p_activity_ids: activityIds, p_tenant_hint: currentTenant.id }
        );
        if (attErr) {
          logger.error('Erro ao buscar anexos:', attErr.message);
        } else if (attachmentsData?.length) {
          const attachmentsByActivity = new Map<string, ActivityAttachment[]>();
          (attachmentsData as any[]).forEach(att => {
            const list = attachmentsByActivity.get(att.activity_id) || [];
            list.push({
              id: att.id,
              file_name: att.file_name,
              file_type: att.file_type,
              file_size: att.file_size,
              public_url: att.public_url,
              thumbnail_url: att.thumbnail_url,
              mime_type: att.mime_type,
            } as ActivityAttachment);
            attachmentsByActivity.set(att.activity_id, list);
          });
          for (const act of activities) {
            act.attachments = attachmentsByActivity.get(act.id) || [];
          }
        }
      }

      logger.debug(`Página carregada: ${activities.length} atividades do deal`);
      return activities;
    },
    initialPageParam: null as CursorParam | null,
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      const lastItem = lastPage[lastPage.length - 1];
      return {
        cursor_created_at: lastItem.created_at,
        cursor_id: lastItem.id,
      } as CursorParam;
    },
    enabled: isEnabled,
    staleTime: 30 * 1000,
  });

  // Flatten de todas as páginas
  const allActivities = useMemo(() => {
    return infiniteQuery.data?.pages.flat() || [];
  }, [infiniteQuery.data?.pages]);

  /**
   * Invalida caches relacionados
   */
  const invalidateCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated', dealId] });
    queryClient.invalidateQueries({ queryKey: ['crm-deal-activities', dealId] });
    queryClient.invalidateQueries({ queryKey: ['crm-activities-feed'] });
  };

  // Mutation: concluir atividade (RPC)
  const completeActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('complete_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade concluída!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao concluir atividade: ${error.message}`);
    },
  });

  // Mutation: reabrir atividade (RPC)
  const reopenActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('reopen_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade reaberta!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao reabrir atividade: ${error.message}`);
    },
  });

  // Mutation: atualizar atividade manual (RPC)
  const updateActivity = useMutation({
    mutationFn: async (input: UpdateActivityInput) => {
      // Monta payload apenas com chaves que vieram no input
      const payload: Record<string, unknown> = {};
      if (input.title !== undefined) payload.title = input.title;
      if (input.description !== undefined) payload.description = input.description;
      if (input.scheduled_at !== undefined) {
        payload.scheduled_at = input.scheduled_at
          ? new Date(input.scheduled_at).toISOString()
          : null;
      }

      const { error } = await supabase.rpc('update_deal_activity', {
        p_activity_id: input.activity_id,
        p_payload: payload as any,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade atualizada!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar atividade: ${error.message}`);
    },
  });

  // Mutation: deletar atividade (RPC)
  const deleteActivity = useMutation({
    mutationFn: async (activityId: string) => {
      const { error } = await supabase.rpc('delete_deal_activity', {
        p_activity_id: activityId,
        p_tenant_hint: currentTenant?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateCaches();
      toast.success('Atividade removida!');
    },
    onError: (error: Error) => {
      toast.error(`Erro ao remover atividade: ${error.message}`);
    },
  });

  return {
    activities: allActivities,
    isLoading: infiniteQuery.isLoading,
    error: infiniteQuery.error,
    completeActivity,
    reopenActivity,
    updateActivity,
    deleteActivity,
    refetch: infiniteQuery.refetch,
    // Propriedades de scroll infinito
    hasNextPage: infiniteQuery.hasNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
}
